import React, { useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  BsShare,
  BsPencil,
  BsCheck2,
  BsTrash,
  BsArrowLeft,
  BsClipboard,
} from "react-icons/bs";
import toast from "react-hot-toast";
import { GoogleAnalyticsContext } from "../../Hooks/GoogleAnalyticsContext";
import { useAuthenticatedFetch, useFetch } from "../../Hooks/useFetch";
import config from "../../config";
import { YippityUserContext } from "../../Hooks/YippityUserContext";
import { useFirebaseAuth } from "../../Hooks/FirebaseAuth";
import SidebarLayout from "../../Components/Layout/SidebarLayout";

interface Question {
  questionText: string;
  answerText: string;
  canEdit: boolean;
  id: number;
}

interface Quiz {
  name: string;
  authorId: number;
  id: number;
}

const QuestionAnswerCard: React.FC<Question> = (props) => {
  const [editingMode, setEditingMode] = useState(false);
  const [question, setQuestion] = useState(props.questionText);
  const [answer, setAnswer] = useState(props.answerText);
  const [isDeleted, setIsDeleted] = useState(false);
  const { logEvent } = useContext(GoogleAnalyticsContext);

  const { res: updateQuestionResponse, callAPI } = useAuthenticatedFetch({
    url: config.API_URI + `/question/${props.id}`,
    method: "PUT",
  });

  const { res: deleteQuestionResponse, callAPI: deleteQuestion } =
    useAuthenticatedFetch({
      url: config.API_URI + `/question/${props.id}`,
      method: "DELETE",
    });

  const handleCopy = async () => {
    const finalText = answer ? question + "\n" + answer : question;
    await navigator.clipboard.writeText(finalText);
    toast.success("Copied question and answer to clipboard");
    logEvent("Interaction", "Copy");
  };

  const handleEdit = () => {
    setEditingMode(true);
    logEvent("Interaction", "Edit");
  };

  const handleSave = () => {
    setEditingMode(false);
    logEvent("Interaction", "Done");
    callAPI(
      JSON.stringify({
        questionText: question,
        answerText: answer,
      })
    );
  };

  const handleDelete = () => {
    setIsDeleted(true);
    deleteQuestion();
    logEvent("Interaction", "Delete");
  };

  if (isDeleted) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden mb-4">
      <div className="p-5">
        {editingMode ? (
          <textarea
            className="w-full p-3 border border-gray-200 rounded mb-3 focus:outline-none focus:ring-2 focus:ring-yellow-400"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            rows={3}
          />
        ) : (
          <div className="font-medium text-gray-800 mb-3">{question}</div>
        )}

        {answer &&
          (editingMode ? (
            <textarea
              className="w-full p-3 border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-yellow-400"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              rows={4}
            />
          ) : (
            <div className="text-gray-600 mt-2 bg-gray-50 p-4 rounded-md">
              {answer}
            </div>
          ))}
      </div>

      <div className="bg-gray-50 px-5 py-3 border-t border-gray-100 flex justify-end space-x-2">
        {editingMode ? (
          <button
            onClick={handleSave}
            className="inline-flex items-center px-3 py-1.5 bg-yellow-400 hover:bg-yellow-500 text-gray-800 rounded text-sm"
          >
            <BsCheck2 className="mr-1" /> Save
          </button>
        ) : (
          <>
            <button
              onClick={handleCopy}
              className="inline-flex items-center px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-sm"
            >
              <BsClipboard className="mr-1" /> Copy
            </button>

            {props.canEdit && (
              <>
                <button
                  onClick={handleEdit}
                  className="inline-flex items-center px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-sm"
                >
                  <BsPencil className="mr-1" /> Edit
                </button>

                <button
                  onClick={handleDelete}
                  className="inline-flex items-center px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-sm"
                >
                  <BsTrash className="mr-1" /> Delete
                </button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

function QuizHeader({
  quizData,
  canEdit,
}: {
  quizData?: Quiz;
  canEdit: boolean;
}) {
  const [name, setName] = useState(quizData?.name || "");
  const [isEditing, setIsEditing] = useState(false);
  const navigate = useNavigate();
  const { logEvent } = useContext(GoogleAnalyticsContext);

  const { res: updateQuizResponse, callAPI } = useAuthenticatedFetch({
    url: config.API_URI + `/quiz/${quizData?.id}`,
    method: "PUT",
  });

  useEffect(() => {
    if (updateQuizResponse.statusCode === 200) {
      const { quiz } = updateQuizResponse.data;
      setName(quiz.name);
    }
  }, [updateQuizResponse]);

  useEffect(() => {
    if (quizData) {
      setName(quizData.name);
    }
  }, [quizData]);

  const handleEdit = () => {
    setIsEditing(true);
    logEvent("Interaction", "Edit Title");
  };

  const handleSave = () => {
    callAPI(
      JSON.stringify({
        name,
      })
    );
    setIsEditing(false);
    logEvent("Interaction", "Save Title");
  };

  const handleShare = async () => {
    await navigator.clipboard.writeText(
      `https://yippity.io/quiz/${quizData?.id}`
    );
    toast.success("Copied link to clipboard! Your quiz is live!");
    logEvent("Interaction", "Share");
  };

  return (
    <div className="mb-6">
      <div className="flex items-center text-gray-600 mb-4">
        <button
          onClick={() => navigate("/quiz")}
          className="inline-flex items-center hover:text-gray-900"
        >
          <BsArrowLeft className="mr-2" /> Back to Quizzes
        </button>
      </div>

      <div className="flex justify-between items-center">
        <div className="flex-1">
          {isEditing ? (
            <div className="flex items-center">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="flex-1 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400"
                placeholder="Quiz name"
              />
              <button
                onClick={handleSave}
                className="ml-2 p-2 bg-yellow-400 hover:bg-yellow-500 text-gray-800 rounded-md"
              >
                <BsCheck2 size={20} />
              </button>
            </div>
          ) : (
            <div className="flex items-center">
              <h1 className="text-3xl font-bold text-gray-800">{name}</h1>
              {canEdit && (
                <button
                  onClick={handleEdit}
                  className="ml-3 p-1 text-gray-400 hover:text-gray-700"
                >
                  <BsPencil size={18} />
                </button>
              )}
            </div>
          )}
        </div>

        <button
          onClick={handleShare}
          className="ml-4 py-2 px-4 bg-yellow-400 hover:bg-yellow-500 text-gray-800 rounded-md inline-flex items-center"
        >
          <BsShare className="mr-2" /> Share
        </button>
      </div>
    </div>
  );
}

export default function Quiz() {
  const { quizId } = useParams();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [quiz, setQuiz] = useState<Quiz>();
  const [isLoading, setIsLoading] = useState(true);

  const { res, callAPI } = useFetch({
    url: config.API_URI + `/quiz/${quizId}`,
    method: "GET",
  });

  const firebaseAuth = useFirebaseAuth();
  const { user } = useContext(YippityUserContext);
  const canEdit =
    !!firebaseAuth && !!user && !!quiz && user.id === quiz.authorId;

  useEffect(() => {
    callAPI();
  }, []);

  useEffect(() => {
    if (res.statusCode === 200) {
      const { questions, quiz } = res.data;
      setQuestions(questions);
      setQuiz(quiz);
      setIsLoading(false);
    }
  }, [res]);

  return (
    <SidebarLayout>
      <div className="max-w-3xl mx-auto">
        <QuizHeader quizData={quiz} canEdit={canEdit} />

        {isLoading ? (
          <div className="space-y-4 animate-pulse">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="bg-white rounded-lg shadow-sm h-48 border border-gray-100"
              ></div>
            ))}
          </div>
        ) : questions.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <h3 className="font-semibold text-xl text-gray-800 mb-2">
              No questions yet
            </h3>
            <p className="text-gray-600">
              This quiz doesn't have any questions
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {questions.map((question, i) => (
              <QuestionAnswerCard
                key={i}
                questionText={question.questionText}
                answerText={question.answerText}
                canEdit={canEdit}
                id={question.id}
              />
            ))}
          </div>
        )}
      </div>
    </SidebarLayout>
  );
}
